import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { ChevronDown, ChevronUp, Clock, Eye, RefreshCw } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	type AvailableDeckMode,
	type DeckType,
	DeckType as DeckTypes,
} from "src/api/flashcardDecks";
import { Menu, type MenuEntry } from "src/components/Default/LekUI/Menu";
import {
	useChangeDeckMode,
	useGetAvailableDeckModes,
} from "src/hooks/useFlashcards";

interface DeckModeMenuTriggerParams {
	onClick: (e: React.MouseEvent) => void;
	anchor: HTMLElement | null;
	isLoading: boolean;
}

interface DeckModeMenuProps {
	deckId: number;
	currentMode?: DeckType;
	className?: string;
	triggerWrapperClassName?: string;
	menuPopperClassName?: string;
	onModeChanged?: () => void;
	renderTrigger?: (params: DeckModeMenuTriggerParams) => ReactNode;
}

interface ModeMeta {
	titleKey: string;
	titleFallback: string;
	descriptionKey: string;
	descriptionFallback: string;
	Icon: ReactNode;
	testId: string;
}

const MODE_META: Record<DeckType, ModeMeta> = {
	[DeckTypes.SpacedRepetition]: {
		titleKey: "decks.modeMenu.spacedRepetition.title",
		titleFallback: "Spaced repetiton",
		descriptionKey: "decks.modeMenu.spacedRepetition.description",
		descriptionFallback:
			"Odpowiadaj na pytania i oceniaj swoją wiedzę. Fiszki, które znasz, wracają rzadziej, a te trudniejsze, częściej.",
		Icon: <Clock className="icon-normal" />,
		testId: "deck-mode-spaced-repetition",
	},
	[DeckTypes.KnowDontKnow]: {
		titleKey: "decks.modeMenu.knowDontKnow.title",
		titleFallback: "Znam/ nie znam",
		descriptionKey: "decks.modeMenu.knowDontKnow.description",
		descriptionFallback:
			"Oceń, czy znasz odpowiedź. Fiszki oznaczone „Znam” wracają rzadziej, a te z „Nie znam”, częściej.",
		Icon: <RefreshCw className="icon-normal" />,
		testId: "deck-mode-know-dont-know",
	},
	[DeckTypes.View]: {
		titleKey: "decks.modeMenu.view.title",
		titleFallback: "Przeglądanie",
		descriptionKey: "decks.modeMenu.view.description",
		descriptionFallback: "Przeglądaj fiszki i ucz się w swoim tempie",
		Icon: <Eye className="icon-normal" />,
		testId: "deck-mode-view",
	},
};

export function DeckModeMenu({
	deckId,
	currentMode,
	className,
	triggerWrapperClassName,
	menuPopperClassName,
	onModeChanged,
	renderTrigger,
}: DeckModeMenuProps) {
	const { t } = useTranslation();
	const [opened, setOpened] = useState(false);
	const { data: modes, isLoading: isModesLoading } = useGetAvailableDeckModes(
		deckId,
		opened,
	);
	const { mutateAsync: changeMode, isPending: isChanging } =
		useChangeDeckMode();

	const handleSelect = async (mode: AvailableDeckMode) => {
		await changeMode({ deckId, type: mode.type, reset: mode.reset });
		onModeChanged?.();
	};

	const buildItems = (): (MenuEntry | null)[] => {
		if (isModesLoading) {
			return [
				{
					title: t("decks.modeMenu.loading", "Ładowanie…"),
					disabled: true,
				},
			];
		}

		return (modes ?? []).map((mode) => {
			const meta = MODE_META[mode.type];
			if (!meta) {
				return null;
			}
			const title = t(meta.titleKey, meta.titleFallback);
			return {
				title: mode.reset
					? `${title} ${t("decks.modeMenu.repeatSuffix", "(powtórka)")}`
					: title,
				description: t(meta.descriptionKey, meta.descriptionFallback),
				Icon: meta.Icon,
				selected: mode.type === currentMode,
				disabled: !mode.isActive,
				onClick: mode.isActive ? () => handleSelect(mode) : undefined,
				testId: meta.testId,
			};
		});
	};

	const defaultTrigger = ({
		onClick,
		anchor,
		isLoading,
	}: DeckModeMenuTriggerParams) => (
		<IconButton
			onClick={onClick}
			data-test-id="deck-mode-menu-trigger"
			disableRipple
			disabled={isLoading}
			className="deck-type-switcher"
		>
			<span className="text-grey deck-type-switcher--label">
				{t("decks.modeMenu.trigger", "Tryb")}
				{currentMode && ":"}
			</span>
			{currentMode && (
				<span>
					{t(
						MODE_META[currentMode].titleKey,
						MODE_META[currentMode].titleFallback,
					)}
				</span>
			)}
			{isLoading ? (
				<CircularProgress size={16} className="text-primary" />
			) : anchor ? (
				<ChevronUp className="icon-super-small" />
			) : (
				<ChevronDown className="icon-super-small" />
			)}
		</IconButton>
	);

	const triggerRenderer = (params: {
		onClick: (e: React.MouseEvent) => void;
		anchor: HTMLElement | null;
	}) => {
		const enhanced: DeckModeMenuTriggerParams = {
			onClick: (e) => {
				setOpened(true);
				params.onClick(e);
			},
			anchor: params.anchor,
			isLoading: isChanging,
		};
		return (renderTrigger ?? defaultTrigger)(enhanced);
	};

	return (
		<Menu
			closeAfterClick
			anchorId="deck-mode-menu"
			menuPopperClassName={menuPopperClassName ?? "max-w-400"}
			itemsClassName="flex flex-col gap-4"
			className={className}
			triggerWrapperClassName={triggerWrapperClassName}
			renderTrigger={triggerRenderer}
			title={t("decks.modeMenu.header", "Wybierz tryb rozwiązywania zestawu")}
			titleClassName="mb-8"
			items={buildItems()}
		/>
	);
}
